import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { MiscJobDetailsViewModel } from '@/features/jobs/details/misc-type/MiscJobDetailsViewModel'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import type { QuestionsDisplayViewModel } from '@/features/jobs/details/QuestionsDisplayViewModel'
import type { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import type { JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import type { JobBillingDetailsViewModel } from '@/features/jobs/details/JobBillingDetailsViewModel'
import type { JobScopeChangesDisplayViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import type { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import type { JobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { computed } from 'mobx'
import { memoizeAsync } from '@/utils/memoize-decorator'
import type {
  EnsureTermsAcceptanceFn,
  EnsureTermsAcceptanceResult,
} from '@/features/account'
import { filter, merge, type Observable } from 'rxjs'
import { whileActive, withAbortSignal } from '@/utils/rx-util'
import { share } from 'rxjs/operators'
import type { StartHandoffFn } from '@/features/shell'
import type { RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { ignoreAbortError } from '@/utils/abort-util'
import type { GetCurrentMemberFn } from '@/features/iam'

/**
 * Type-specific view models.
 */
export type JobTypeDetailsViewModel =
  | TaxJobDetailsViewModel
  | ConsultationJobDetailsViewModel
  | MiscJobDetailsViewModel
  | BookkeepingJobDetailsViewModel

/**
 * Job details view model.
 */
export class JobDetailsViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  /**
   * Observable that emits the outcome of terms acceptance.
   * Can be 'Accepted', 'Declined', or 'Aborted'.
   */
  readonly termsOutcome$: Observable<EnsureTermsAcceptanceResult> =
    withAbortSignal(async (signal) => {
      // Go through hand-off first.
      await this._startHandoff(this.job.workspace, signal)
      return this._ensureTermsAcceptance(this.job.workspace.id, signal)
    }).pipe(
      // Only while the VM is active.
      whileActive(this.lifecycle.active$),
      share(),
    )

  /**
   * Observable that emits when the user should be "bounced" (redirected) from the job details page.
   * This includes when they decline terms or any job type specific bounce conditions.
   */
  readonly bounce$: Observable<unknown>

  constructor(
    public readonly job: SubmittedJob,
    public readonly typeVm: JobTypeDetailsViewModel,
    public readonly questionsDisplayVm: QuestionsDisplayViewModel,
    public readonly docsVm: JobDocumentsViewModel,
    public readonly jobBillingDetailsVm: JobBillingDetailsViewModel,
    public readonly jobScopeChangesDisplayVm: JobScopeChangesDisplayViewModel,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _ensureTermsAcceptance: EnsureTermsAcceptanceFn,
    private readonly _startHandoff: StartHandoffFn,
    public readonly ratingsVm: RatingsViewModel,
  ) {
    this.bounce$ = merge(
      this.termsOutcome$.pipe(filter((outcome) => outcome === 'Declined')),
      // Include job type specific bounce conditions if they exist.
      typeVm.bounce$,
    )

    this.lifecycle.manage(typeVm)
    this.lifecycle.manage(questionsDisplayVm)
    this.lifecycle.manage(jobScopeChangesDisplayVm)
    this.lifecycle.manage(this.termsOutcome$)
    this.lifecycle.manage(this.bounce$)
    this.lifecycle.manage(ratingsVm)
  }

  /**
   * The conversation view model.
   */
  @computed
  get conversationVm(): JobConversationViewModel {
    // Every job type has a conversation, which means it'll have some type of
    // conversation VM.
    return this.typeVm.jobConversationVm
  }

  /**
   * Whether the billing tab is enabled.
   */
  @computed
  get isBillingTabEnabled() {
    return (
      this.job.isBillingEnabled &&
      this._getCurrentMember().permissions.viewJobPricing
    )
  }

  /** @inheritDoc */
  activate() {
    this.lifecycle.activate()

    // Fetch the conversation, needed so we can get the unread count and display it
    // in the UI, even when not on the messages tab. We'll do it in the background
    // because we don't want to block rendering.
    void this.conversationVm
      .fetchUnreadCount(this.lifecycle.signal)
      .catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this.lifecycle.deactivate()
  }

  /**
   * Fetches data needed for the view to be able to render.
   * This is only called once per job.
   */
  @memoizeAsync()
  async fetch(signal?: AbortSignal) {
    await this.typeVm.fetch(signal)
  }
}
